import { Separator } from "@monbits/ui";
import * as React from "react";
import type { AuditLog, AuditStatus } from "src/types";
import { StatusBadge } from "@/components/status-badge";
import { formatAuditDateTime, getCategoryConfig } from "@/utils/audit-log";

const STATUS_BADGE_MAP: Record<AuditStatus, "success" | "error" | "warning"> = {
	success: "success",
	failure: "error",
	partial: "warning",
};

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const renderJson = (value: unknown) => (
	<pre className="whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-xs font-mono text-foreground/90">
		{JSON.stringify(value, null, 2)}
	</pre>
);

interface AuditLogDrawerContentProps {
	log: AuditLog;
}

const AuditLogDrawerContent: React.FC<AuditLogDrawerContentProps> = ({
	log,
}) => {
	const metadataEntries = React.useMemo(() => {
		if (!log.metadata || !isRecord(log.metadata))
			return [] as [string, unknown][];
		return Object.entries(log.metadata);
	}, [log.metadata]);

	const metadataReason = React.useMemo(() => {
		const entry = metadataEntries.find(
			([key, value]) => key === "reason" && typeof value === "string",
		);
		return entry ? String(entry[1]) : null;
	}, [metadataEntries]);

	const metadataErrorDetail = React.useMemo(() => {
		const entry = metadataEntries.find(
			([key, value]) => key === "error" && typeof value === "string",
		);
		return entry ? String(entry[1]) : null;
	}, [metadataEntries]);

	const filteredMetadataEntries = React.useMemo(
		() =>
			metadataEntries.filter(([key]) => key !== "reason" && key !== "error"),
		[metadataEntries],
	);

	const changesRecord = React.useMemo(() => {
		if (!log.changes || !isRecord(log.changes)) return null;
		return log.changes;
	}, [log.changes]);

	const beforeChanges = React.useMemo(() => {
		if (!changesRecord) return null;
		const maybeBefore = (changesRecord as Record<string, unknown>).before;
		return isRecord(maybeBefore) ? maybeBefore : null;
	}, [changesRecord]);

	const afterChanges = React.useMemo(() => {
		if (!changesRecord) return null;
		const maybeAfter = (changesRecord as Record<string, unknown>).after;
		return isRecord(maybeAfter) ? maybeAfter : null;
	}, [changesRecord]);

	const remainingChanges = React.useMemo(() => {
		if (!changesRecord) return null;

		const keys = Object.keys(changesRecord);
		const hasOnlyBeforeAfter = keys.every(
			(key) => key === "before" || key === "after",
		);
		return hasOnlyBeforeAfter ? null : changesRecord;
	}, [changesRecord]);

	return (
		<div className="px-6 pb-6 space-y-6">
			{/* Error/Reason Alerts */}
			{(log.errorMessage || metadataErrorDetail) && (
				<div className="flex items-start gap-3 p-4 border rounded-lg bg-red-50 dark:bg-red-950/30">
					<div className="flex-1 space-y-1">
						<div className="font-medium text-sm text-red-900 dark:text-red-200">
							Error
						</div>
						<div className="text-sm text-red-700 dark:text-red-400">
							{log.errorMessage || metadataErrorDetail}
						</div>
					</div>
				</div>
			)}

			{metadataReason && (
				<div className="flex items-start gap-3 p-4 border rounded-lg bg-amber-50 dark:bg-amber-950/30">
					<div className="flex-1 space-y-1">
						<div className="font-medium text-sm text-amber-900 dark:text-amber-200">
							Reason
						</div>
						<div className="text-sm text-amber-700 dark:text-amber-400">
							{metadataReason}
						</div>
					</div>
				</div>
			)}

			{/* Summary */}
			<div className="space-y-3">
				<h3 className="text-sm font-semibold">Summary</h3>
				<div className="space-y-3">
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Description
						</div>
						<div className="text-sm">{log.description}</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Status
						</div>
						<StatusBadge type={STATUS_BADGE_MAP[log.status]}>
							{log.status}
						</StatusBadge>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Performed By
						</div>
						<div className="text-sm">
							{log.actor.email ||
								`${log.actor.type}${log.actor.id ? ` #${log.actor.id}` : ""}`}
						</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Category
						</div>
						<div className="text-sm">
							{getCategoryConfig(log.actionCategory).label}
						</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Timestamp
						</div>
						<div className="text-sm">{formatAuditDateTime(log.createdAt)}</div>
					</div>
				</div>
			</div>

			<Separator />

			{/* Request Context */}
			<div className="space-y-3">
				<h3 className="text-sm font-semibold">Request Context</h3>
				<div className="space-y-3">
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Request ID
						</div>
						{log.requestId ? (
							<div className="font-mono text-sm break-all">{log.requestId}</div>
						) : (
							<div className="text-sm italic text-muted-foreground">
								Not captured
							</div>
						)}
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							IP Address
						</div>
						{log.ipAddress ? (
							<div className="font-mono text-sm">{log.ipAddress}</div>
						) : (
							<div className="text-sm italic text-muted-foreground">
								Not captured
							</div>
						)}
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							User Agent
						</div>
						{log.userAgent ? (
							<div className="text-sm break-all">{log.userAgent}</div>
						) : (
							<div className="text-sm italic text-muted-foreground">
								Not captured
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Changes */}
			{(beforeChanges || afterChanges || remainingChanges) && (
				<>
					<Separator />
					<div className="space-y-3">
						<h3 className="text-sm font-semibold">Changes</h3>
						<div className="space-y-4">
							{beforeChanges && afterChanges ? (
								<>
									<div>
										<div className="text-xs font-medium text-muted-foreground mb-2">
											Before
										</div>
										{renderJson(beforeChanges)}
									</div>
									<div>
										<div className="text-xs font-medium text-muted-foreground mb-2">
											After
										</div>
										{renderJson(afterChanges)}
									</div>
								</>
							) : remainingChanges ? (
								<div>
									<div className="text-xs font-medium text-muted-foreground mb-2">
										Details
									</div>
									{renderJson(remainingChanges)}
								</div>
							) : null}
						</div>
					</div>
				</>
			)}

			{/* Additional Metadata */}
			{filteredMetadataEntries.length > 0 && (
				<>
					<Separator />
					<div className="space-y-3">
						<h3 className="text-sm font-semibold">Additional Context</h3>
						<div className="space-y-3">
							{filteredMetadataEntries.map(([key, value]) => (
								<div key={key}>
									<div className="text-xs font-medium text-muted-foreground mb-1">
										{key}
									</div>
									{typeof value === "string" ||
									typeof value === "number" ||
									typeof value === "boolean" ||
									value === null ? (
										<div className="text-sm">
											{value === null ? "—" : String(value)}
										</div>
									) : (
										renderJson(value)
									)}
								</div>
							))}
						</div>
					</div>
				</>
			)}

			{/* Technical Details */}
			<Separator />
			<div className="space-y-3">
				<h3 className="text-sm font-semibold">Technical Details</h3>
				<div className="space-y-3">
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Action
						</div>
						<div className="font-mono text-sm">{log.action}</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Resource Type
						</div>
						<div className="font-mono text-sm">{log.resource.type}</div>
					</div>
					{log.resource.id && (
						<div>
							<div className="text-xs font-medium text-muted-foreground mb-1">
								Resource ID
							</div>
							<div className="font-mono text-sm">{log.resource.id}</div>
						</div>
					)}
					{log.resource.identifier && (
						<div>
							<div className="text-xs font-medium text-muted-foreground mb-1">
								Resource Identifier
							</div>
							<div className="text-sm">{log.resource.identifier}</div>
						</div>
					)}
					{log.actor.id && (
						<div>
							<div className="text-xs font-medium text-muted-foreground mb-1">
								Actor ID
							</div>
							<div className="font-mono text-sm">#{log.actor.id}</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export { AuditLogDrawerContent };
